import * as Yup from 'yup';
import { deleteRequest } from '../../../../../services/httpService';

export type CounselingTypeKey = 'psychological' | 'treatment' | 'financial';

export interface SessionDetails {
  id?: number | null;
  patient: number |null;
  counseling_date: string | null;
  counseling_time: string | null;
  counselor_name: string | null;
  next_visit_date: string | null;
  counseling_stage: string | null;
  counseling_stage_label?: string | null;
  counseling_remark: string | null;
  source_of_reference: string | null;
  source_of_reference_label?: string | null;
  reference_details: string | null;
  treatment_advice: number | null;
  treatment_doctor: number | null;
  treatment_doctor_label?: string | null;
  treatment_doctor_name?: string | null;
  is_freezed: boolean;
  tenant?: number | null;
  created_on?: string;
  updated_on?: string;
 }

export interface UploadedFile {
  name: string;
  base64: string;
}

export interface CounselingType {
  id?: number | null;
  type: {
    id: number | null;
    key: string;
    name: string;
  };
  type_label?: string | null;
  date: string | null;
  remark: string | null;
  report_file?: UploadedFile | null | { image?: string; id?: number; file_name?: string; file_size?: number; file_extension?: string }[];
  created_on?: string;
  updated_on?: string;
  enabled?: boolean;
}

export interface CounselingFeedback {
  id?: number | null;
  feedback_date: string;
  // For POST we use code/string; API responses may return an object
  patient_response: string | null;
  patient_response_label?: string | null;
  patient_response_obj?: {
    id: number | null;
    key: string;
    name: string;
  } | null;
  patient_remarks: string;
  feedback: string;
  created_on?: string;
  updated_on?: string;
}

export interface CounselingDetailsFormValues {
  session_details: SessionDetails;
  counseling_types: CounselingType[];
  feedback: CounselingFeedback[];
}


export interface PostCounselingType {
  type: string | number | null; // Can be name (string) or id (number)
  date: string | null;          // ISO date
  remark: string | null;        // remark text
  report_file?: { image?: string; file_name?: string; file_size?: number; file_extension?: string }[]; // uploaded images array
}

export interface PostFeedbackItem {
  feedback_date: string ;         // ISO date
  patient_response: string | null; // numeric code or null
  patient_remarks: string;       // patient remarks text
  feedback: string;              // counselor feedback text
}

export interface CounselingDetailsPostPayload {
  session_details: SessionDetails;
  counseling_types: PostCounselingType[];
  feedback: PostFeedbackItem[];
}


const pad2 = (n: number) => String(n).padStart(2, '0');
const currentDate = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};
const currentTime = () => {
  const d = new Date();
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

export const initialValues: CounselingDetailsFormValues = {
  session_details: {
    id:null,
    patient: null,
    counseling_date: currentDate(),
    counseling_time: currentTime(),
    counselor_name: '',
    next_visit_date: null,
    counseling_stage: '',
    counseling_remark: '',
    source_of_reference: null,
    reference_details: null,
    treatment_advice: null,
    treatment_doctor: null,
    is_freezed: false,
  },
  counseling_types: [
    {
      id: null,
      type: { id: 1, key: 'COUNSELING_TYPE', name: 'Treatment' },
      type_label: null,
      date: null,
      remark: null,
      report_file: null,
      created_on: '',
      updated_on: '',
      enabled: false,
    },
    {
      id: null,
      type: { id: 2, key: 'COUNSELING_TYPE', name: 'Psychological' },
      type_label: null,
      date: null,
      remark: null,
      report_file: null,
      created_on: '',
      updated_on: '',
      enabled: false,
    },
    {
      id: null,
      type: { id: 3, key: 'COUNSELING_TYPE', name: 'Financial' },
      type_label: null,
      date: null,
      remark: null,
      report_file: null,
      created_on: '',
      updated_on: '',
      enabled: false,
    },
  ],
  feedback: [
    { feedback_date: currentDate(), patient_response: null, patient_remarks: '', feedback: '' },
  ],
};
export interface SourceOfReference {
  id: number;
  type: string;
  name: string;
  code: string;
  related_name: string;
  is_active: boolean;
  translations: {
    [lang: string]: string;
  };
}

// === Config master interfaces ===
export interface ConfigItem {
  id: number;
  code: string;
  name: string;
  translation: Record<string, string>;
}

export interface CounselingConfigResponse {
  COUNSELING_STAGE: ConfigItem[];
  COUNSELING_TYPE: ConfigItem[];
  PATIENT_RESPONSE: ConfigItem[];
}

export const validationSchema = Yup.object({
  session_details: Yup.object({
    counseling_date: Yup.string().required('Counseling Date is required'),
    counseling_time: Yup.string().required('Counseling Time is required'),
    counselor_name: Yup.string().required('Counselor Name is required'),
    next_visit_date: Yup.string().nullable(),
    counseling_stage: Yup.string().required('Counseling Stage is required'),
    counseling_remark: Yup.string().nullable(),
    source_of_reference: Yup.string().nullable(),
    reference_details: Yup.string().nullable(),
    treatment_advice: Yup.number().required('Treatment Advice is required'),
    treatment_doctor: Yup.number().required('Treatment Doctor is required'),
    is_freezed: Yup.boolean(),
  }),
  counseling_types: Yup.array().of(
    Yup.object({
      type: Yup.mixed().required('Type is required'),
      date: Yup.string().when('enabled', {
        is: true,
        then: schema => schema.required('Date is required'),
        otherwise: schema => schema.notRequired(),
      }),
      remark: Yup.string().notRequired(),
      report_file: Yup.mixed().notRequired(),
      enabled: Yup.boolean(),
    })
  ),
  feedback: Yup.array().of(
    Yup.object({
      feedback_date: Yup.string().required('Feedback Date is required'),
      patient_response: Yup.string().required('Patient Response is required'),
      patient_remarks: Yup.string().nullable(),
      feedback: Yup.string().nullable(),
    })
  ).min(1, 'At least one feedback is required'),
});


export function getSrcOfReference(): string {
  return 'clinics/patient-config/?type=source_of_reference';
}

export function CounsellingDetailsURL(): string {
  return 'counseling-sessions/';
}

export function getCounselingConfig(): string {
  return 'counseling-sessions/config';
}

// Function to delete attachment from counseling session
export const deleteCounselingAttachment = (
  attachmentId: number
): Promise<void> => {
  const url = `counseling-sessions/file_delete/${attachmentId}/`;
  return deleteRequest(url);
};
